import React, { useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl, useLocation, history } from 'umi';
import { useField, useForm } from 'react-final-form-hooks';
import {
  Button,
  DatePicker,
  Form,
  Input,
  notification,
  Select,
  Modal,
  ConfigProvider,
} from 'tea-component';
import moment, { Moment } from 'moment';
import { useLinkPath, useLocalText } from '@/pages/dashboard/utils/customHook';
import { trim } from '@/utils/utils';
import { getEventTypeList, saveEventRecord } from '@/pages/dashboard/services/event';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { ADD_EVENT_FORM_ERROR_CODE, ADD_EVENT_TEXT_LOCAL_CONFIG, ADD_EVENT_TIME_OPTIONS } from '../_config/constant';
import SelectWithCustomize from '@/pages/dashboard/components/SelectWithCustomize';
import SelectorMultiple from '@/pages/dashboard/components/SelectorMultiple';
import { loadFilterList } from '@/pages/dashboard/services/dashboard';
import './index.scss';

interface Country {
  country_code: string;
  country_name_ch: string;
  country_name_en: string;
  deviation: number;
}

const { RangePicker } = DatePicker;
function AddEventForm({ onClose, gameCode, dispatch, editConfig }) {
  const location = useLocation();
  const { query } = history.location;
  const { formatMessage } = useIntl();
  const local = getLocale();
  const { isEdit, data } = editConfig;
  const localText = useLocalText(ADD_EVENT_TEXT_LOCAL_CONFIG);
  const [isGetCountry, setIsGetCountry] = useState(false);
  const [isGetPlatform, setIsGetPlatform] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [eventTypeData, setEventTypeData] = useState([]);
  const [eventTypeError, setEventTypeError] = useState(null);
  const [formErrorCode, setFormErrorCode] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const jumpLinkArray = useLinkPath('eventAnalysis/eventDetails');
  const jumpLink = Array.isArray(jumpLinkArray) ? jumpLinkArray[0] : jumpLinkArray;
  const realtimeLinks = useLinkPath([
    'realtime/revenue',
    'realtime/users',
    'realtime/users/users',
  ]);
  const currentPathname = location.pathname;

  const isEn = local === 'en-US';
  const { id, country: countryData, platform: platformData, start_time, end_time,
    event_type, event_title_en, event_desc_en, event_link_url } = data || {};
  const startTime = start_time?.slice(0, 10);
  const endTime = end_time?.slice(0, 10);
  const isSigle = startTime === endTime;
  const eventTime = end_time?.slice(-8);
  const initialValues = !isEdit ? {
    date: [moment(Date.now()), moment(Date.now())],
    time: '00:00:00',
    eventType: {},
    title: '',
    country: ['255'],
    platform: ['255'],
    description: '',
    link: '',
  } : {
    date: [moment(startTime), moment(endTime)],
    time: isSigle ? (eventTime || '00:00:00') : '00:00:00',
    eventType: {
      // value: event_type?.id,
      selectedValue: event_type?.name_en,
      // name_en: event_type?.name_en,
      // label:event_type?.name_en,
      text: isEn ? event_type?.name_en : event_type?.name_cn || event_type?.name_en,
    },
    title: event_title_en,
    country: countryData?.split(',') || ['255'],
    platform: platformData?.split(',') || ['255'],
    description: event_desc_en,
    link: event_link_url,
  };

  const { form, values, validating, hasValidationErrors, handleSubmit } =
    useForm({
      destroyOnUnregister: true,
      onSubmit: () => { },
      initialValues,
      initialValuesEqual: () => true,
      validate: ({
        date = '',
        eventType = '',
        title,
        description,
        time = '',
      }) => {
        let titleErrorMessage: string | undefined = undefined;
        if (trim(title)?.length > 30)
          titleErrorMessage = formatMessage(
            { id: 'form_error_message_empty_limit' },
            { field: localText.title, length: 30 },
          );
        else if (trim(title)?.length < 1)
          titleErrorMessage = formatMessage(
            { id: 'form_error_message_not_empty' },
            { field: localText.title },
          );
        else if (formErrorCode == 50001)
          titleErrorMessage = formatMessage(
            { id: 'form_error_message_exists' },
            { field: localText.title },
          );
        return {
          date: date
            ? undefined
            : formatMessage(
              { id: 'form_error_message_not_empty' },
              { field: localText.date },
            ),
          time: time
            ? undefined
            : formatMessage(
              { id: 'form_error_message_not_empty' },
              { field: localText.time },
            ),
          eventType:
          eventType.customizedValue || eventType.selectedValue
            ? undefined
            : formatMessage(
              { id: 'form_error_message_not_empty' },
              { field: localText.eventType },
            ),

          title: titleErrorMessage,

          description:
            trim(description)
              ? undefined
              : formatMessage(
                { id: 'form_error_message_not_empty' },
                { field: localText.description },
              ),
        };
      },
    });

  const date = useField('date', form);
  const showTimeSelect = moment(date?.input?.value?.[0])?.format('YYYY-MM-DD') === moment(date?.input?.value?.[1])?.format('YYYY-MM-DD');
  const time = useField('time', form);
  const eventType = useField('eventType', form);
    
  const title = useField('title', form);
  const country = useField('country', form);
  const platform = useField('platform', form);
  const description = useField('description', form);
  const link = useField('link', form);

  useEffect(() => {
    // 请求事件类型列表
    getEventTypeList({ game_code: gameCode })
      .then((res) => {
        setEventTypeError(null);
        setEventTypeData(res);
      })
      .catch((e) => {
        setEventTypeError(e);
      });
    // 请求国家数据
    if (
      !cacheCountryData[gameCode] ||
      cacheCountryData[gameCode].length === 0
    ) {
      const params = {
        game_code: gameCode,
        last_data_metric: '',
        selector_list: ['country', 'os'],
      };
      setCountriesError(null);
      loadFilterList(params)
        .then((res) => {
          // 对国家数据进行排序整理, 筛选修改255数据文本等.
          const countries = res?.country_list || [];
          const findGlobalIndex = countries.findIndex(
            (item: { country_code: string; }) => item.country_code === '255',
          );
          if (findGlobalIndex > -1) countries.splice(findGlobalIndex, 1); // 删除原有255数据
          // 对剩余国家进行排序
          countries.sort((a: { country_name_en: string; }, b: { country_name_en: any; }) =>
            a?.country_name_en?.localeCompare(b?.country_name_en),
          ); // 缓存数据
          cacheCountryData = {
            [gameCode]: [...countries],
          };
          cachePlatformData = {
            [gameCode]: res?.os_list?.filter((v: any) => v?.os_id !== '255') || []
          }
        })
        .catch((e) => {
          setCountriesError(e);
        })
        .finally(() => {
          setIsGetCountry(true);
          setIsGetPlatform(true);
        });
    }
  }, []);
  // 提交方法写在onSubmit里时, 点击cancel按钮也会提交.
  const submitNewEvent = (values:any) => {
    // setSubmitting(true);
    const {
      date,
      eventType,
      title,
      description,
      link,
      time,
      country,
      platform,
    } = values;

    saveEventRecord({
      game_code: gameCode,
      data: [
        {
          event_type: {
            name_en: eventType?.selectedValue || eventType?.customizedValue,
          },
          event_record: {
            id: isEdit ? id : 0,
            period_type: 'daily',
            event_start_date: moment(date?.[0]).format('YYYY-MM-DD'),
            event_end_date: moment(date?.[1]).format('YYYY-MM-DD'),
            event_date: moment(date?.[1]).format('YYYY-MM-DD'),
            event_time: showTimeSelect ? time : '',
            event_title_en: trim(title),
            event_desc_en: trim(description),
            event_link_url: trim(link),
            country: country?.join(','),
            platform: platform?.join(','),
          }
        },

      ],
    })
      .then(() => {
        notification.success({
          className: 'downDataBox',
          duration: 1000,
          description: formatMessage({ id: isEdit ? 'event_edit_success' : 'event_add_success' })
        });
        onClose?.();
        const fromPage = query?.fromPage;
        // history.push(fromPage)
        history.push({
          pathname: jumpLink,
          query: {
            dashboardType: showTimeSelect ? 'realtime' : 'overview',
            date: moment(date?.[1]).format('YYYY-MM-DD'),
            isOverviewAdd: true,
            startDate: moment(date?.[0]).format('YYYY-MM-DD'),
            endDate: moment(date?.[1]).format('YYYY-MM-DD'),
            timeType: 'Day',
            eventType: eventType?.selectedValue,
            fromPage,
            refresh: Date.now(),
          },
        });
        
        if (realtimeLinks.includes(currentPathname)) {
          // 如果当前页面是realtime展示事件的页面就更新状态, 刷新事件数据
          dispatch({
            type: 'dashboard/changeRefreshEvent',
            payload: {
              [values.dashboard]: true,
            },
          });
        }
      })
      .catch((e) => {
        if (ADD_EVENT_FORM_ERROR_CODE.includes(e?.code)) {
          setFormErrorCode(e?.code);
        } else {
          notification.error({
            className: 'downDataBox',
            duration: 1000,
            description: e.msg || formatMessage({ id: isEdit ? 'event_edit_fail' : 'event_add_fail' })
          });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  // 获取国家下拉列表options
  const countryOptions = useMemo(() => {
    if (!Array.isArray(cacheCountryData[gameCode])) return null;
    return cacheCountryData[gameCode].map((country:Country) => {
      const { country_code, country_name_ch, country_name_en, deviation } =
        country || {};
      return {
        value: country_code,
        text: local === 'en-US' ? country_name_en : country_name_ch,
        icon: getCountryFlag(country_code),
        deviation,
      };
    });
  }, [local, isGetCountry]);

  // 获取Platform下拉列表options
  const platformOptions = useMemo(() => {
    if (!Array.isArray(cachePlatformData[gameCode])) return [];
    return cachePlatformData[gameCode]?.map((item: any) => {
      const { os_id, os_name_ch, os_name_en } = item || {};
      return {
        value: os_id,
        text: local === 'en-US' ? os_name_en : os_name_ch
      };
    });
  }, [local, isGetPlatform]);

  const eventTypeOptions = useMemo(() => {
    if (!Array.isArray(eventTypeData)) return [];
    return eventTypeData.map((item:any) => ({
      value: item?.name_en || '',
      text: isEn ? item?.name_en : item?.name_cn || item?.name_en,
    })); //
  }, [eventTypeData, local]);

  return (
    <form onSubmit={handleSubmit}>
      <Modal.Body>
        <Form>
          <Form.Item
            showStatusIcon={false}
            label={<div className="modal-item">{localText.date}</div>}
            required
            status={
              showTimeSelect ?
                (getStatus(date.meta, validating) && getStatus(time.meta, validating)) : getStatus(date.meta, validating)
            }
            message={
              showTimeSelect ?
                ((getStatus(date.meta, validating) === 'error' && date.meta.error) ||
                  (getStatus(time.meta, validating) === 'error' && time.meta.error))
                : getStatus(date.meta, validating) === 'error' && date.meta.error
            }
          >
            <ConfigProvider locale={getLocale() === 'zh-CN' ? 'zh' : "en"}>
              {
                <RangePicker
                  {...date.input}
                  value={date?.input?.value}
                  key={date?.input?.value}
                  className="dbd-settings-range-picker"
                  format={'YYYY-MM-DD'}
                  disabledDate={
                    (date: Moment, start: Moment) => {
                      const isSixMonthBefore = moment(date).isAfter(moment(start).add(6, "month"));
                      const isSixMonthAfter = moment(date).isBefore(moment(start).subtract(2, "month"));
                      if (moment.isMoment(start)) {
                        return (
                          !(
                            isSixMonthBefore || isSixMonthAfter
                          )
                        );
                      } 
                      return true;
                    }
                  }
                />
              }
            </ConfigProvider>
            {showTimeSelect && (
              <Select
                {...time.input}
                className="settings-select"
                appearance="button"
                options={ADD_EVENT_TIME_OPTIONS}
                // placeholder="请选择时间"
                matchButtonWidth
              />
            )}
          </Form.Item>
          <Form.Item
            className="dashboard-modal"
            showStatusIcon={false}
            label={<div className="modal-item">{localText.eventType}</div>}
            required
            status={getStatus(eventType.meta, validating)}
            message={
              getStatus(eventType.meta, validating) === 'error' &&
              eventType.meta.error
            }
          >
            <SelectWithCustomize
              {...eventType.input}
              onChange={(value) => {
                if (value?.selectedValue == 'Verson') {
                  form.change('country', ['255']);
                  form.change('platform', ['255']);
                }
                eventType.input.onChange(value);
              }}
              defaultEventType={initialValues?.eventType}
              loading={!eventTypeOptions.length && !eventTypeError}
              options={eventTypeOptions}
              placeholder={localText.eventTypePlaceholder}
              customizeConfig={{
                placeholder: localText.customizePlaceholder,
                validate: (value) => {
                  const trimValue = trim(value);
                  if (!trimValue) return undefined;
                  if (trimValue.length > 20)
                    return formatMessage(
                      { id: 'form_error_message_limit_length' },
                      { length: 20 },
                    );
                  // 不能和options内容一致.
                  const opt = eventTypeData.find((data) => {
                    const { name_en, name_cn } = data || {};
                    return trimValue === name_en || trimValue === name_cn;
                  });

                  return opt
                    ? formatMessage(
                      { id: 'form_error_message_exists' },
                      { field: trimValue },
                    )
                    : undefined;
                },
              }}
              onClose={() => {
                // 为了在下拉框关闭的时候可以校验字段.
                form.blur('eventType');
              }}
            />
          </Form.Item>
          <Form.Item
            showStatusIcon={false}
            label={<div className="modal-item">{localText.title}</div>}
            required
            status={getStatus(title.meta, validating)}
            message={
              getStatus(title.meta, validating) === 'error' && title.meta.error
            }
          >
            <Input
              {...title.input}
              size="full"
              onFocus={() => {
                formErrorCode && setFormErrorCode(undefined);
                title.input.onFocus();
              }}
              placeholder={localText.titlePlaceholder}
            />
          </Form.Item>

          <Form.Item
            className="form-item"
            showStatusIcon={false}
            label={<div className="modal-item">{localText.country}</div>}
          >
            <SelectorMultiple
              {...country.input}
              key={'country'}
              className='dbd-event-country-drop-down'
              allDescribe={localText.global}
              label={localText.country}
              buttonLabel={''}
              positionFixed={true}
              // defaultValue={["255"]}
              options={countryOptions}
              showTagCount={6}
              disabled={
                values.eventType?.selectedValue == 'Verson'
              }
              loading={!countryOptions && !countriesError}
            />
          </Form.Item>

          <Form.Item
            className="form-item"
            showStatusIcon={false}
            label={<div className="modal-item">{localText.platform}</div>}
          >
            <SelectorMultiple
              {...platform.input}
              key={'platform'}
              className='dbd-event-country-drop-down'
              allDescribe={localText.all}
              label={localText.platform}
              buttonLabel={''}
              // defaultValue={["255"]}
              options={platformOptions}
              positionFixed={true}
              showTagCount={6}
              disabled={
                values.eventType?.selectedValue == 'Verson'
              }
              loading={!platformOptions && !countriesError}
            />
          </Form.Item>

          <Form.Item
            className="dashboard-modal"
            showStatusIcon={false}
            label={<div className="modal-item">{localText.description}</div>}
            required={true}
            status={getStatus(description.meta, validating)}
            message={
              getStatus(description.meta, validating) === 'error' &&
              description.meta.error
            }
          >
            <Input.TextArea
              {...description.input}
              size="full"
              className="dashboard-modal-inputtext"
              disabled={false}
              placeholder={localText.descriptionPlaceholder}
            />
          </Form.Item>
          <Form.Item
            className="dashboard-modal"
            label={localText.link}
            showStatusIcon={false}
            status={getStatus(link.meta, validating)}
            message={
              getStatus(link.meta, validating) === 'error' && link.meta.error
            }
          >
            <Input
              {...link.input}
              size="full"
              disabled={false}
              placeholder={localText.linkPlaceholder}
            />
          </Form.Item>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Form.Action>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={hasValidationErrors || submitting}
            onClick={() => {
              submitNewEvent(values);
            }}
          >
            {localText.confirm}
          </Button>
          <Button type="weak" onClick={onClose}>
            {localText.cancel}
          </Button>
        </Form.Action>
      </Modal.Footer>
    </form>
  );
}

export default AddEventForm;

function getStatus(meta, validating) {
  if (meta.active && validating) {
    return 'validating';
  }
  if (!meta.touched) {
    return null;
  }
  return meta.error ? 'error' : 'success';
}

// 缓存国家下拉选项数据
let cacheCountryData: any = {};
let cachePlatformData: any = {};
